/**
 * useAudioTranscription — Manages audio transcription state and recorder wiring.
 *
 * Extracted from ChatContainer to reduce orchestrator size.
 * Owns: transcription state, mode toggle, dots animation, and audioRecorder config.
 */

import { useState, useCallback, useEffect } from "react";
import useAudioRecorder from "./useAudioRecorder";

export const useAudioTranscription = ({
  currentLanguage,
  realtimeEnabled,
  setInputValue,
}) => {
  const [transcriptionHistory, setTranscriptionHistory] = useState({});
  const [isTranscribingAudio, setIsTranscribingAudio] = useState(false);
  const [transcribingDots, setTranscribingDots] = useState("");
  const [mode, setMode] = useState("transcribe");
  const [realtimeModeEnabled, setRealtimeModeEnabled] = useState(false);

  const handleTranscriptionStart = useCallback(() => {
    setIsTranscribingAudio(true);
  }, []);

  const handleTranscriptionEnd = useCallback(() => {
    setIsTranscribingAudio(false);
  }, []);

  const handleTranscriptionComplete = useCallback(
    (transcription) => {
      setInputValue((prevValue) => {
        const separator = prevValue.trim() ? " " : "";
        return prevValue + separator + transcription;
      });
    },
    [setInputValue],
  );

  const toggleMode = useCallback(() => {
    if (!realtimeEnabled) return;
    setMode((currentMode) =>
      currentMode === "transcribe" ? "realtime" : "transcribe",
    );
  }, [realtimeEnabled]);

  const audioRecorder = useAudioRecorder({
    mode,
    toggleMode,
    transcribeConfig: {
      language: currentLanguage.split("-")[0],
      onTranscriptionComplete: handleTranscriptionComplete,
      onTranscriptionStart: handleTranscriptionStart,
      onTranscriptionEnd: handleTranscriptionEnd,
    },
  });

  // Transcribing dots animation
  useEffect(() => {
    if (!isTranscribingAudio) {
      setTranscribingDots("");
      return;
    }
    const interval = setInterval(() => {
      setTranscribingDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 400);
    return () => clearInterval(interval);
  }, [isTranscribingAudio]);

  // Track transcription history from realtime items
  const updateTranscriptionHistory = useCallback((items) => {
    items.forEach((item) => {
      const messageContent = item.formatted?.transcript || item.formatted?.text;
      if (messageContent && item.id) {
        setTranscriptionHistory((prev) => ({
          ...prev,
          [item.id]: { role: item.role, transcript: messageContent },
        }));
      }
    });
  }, []);

  const resetAudio = useCallback(() => {
    setTranscriptionHistory({});
    setIsTranscribingAudio(false);
    setRealtimeModeEnabled(false);
  }, []);

  return {
    audioRecorder,
    transcriptionHistory,
    isTranscribingAudio,
    transcribingDots,
    mode,
    realtimeModeEnabled,
    setRealtimeModeEnabled,
    updateTranscriptionHistory,
    resetAudio,
  };
};
